<template>
    <div class="mt-10 w-full max-w-3xl">
        <p class="text-white/80 text-sm font-semibold tracking-wide mb-3">
            {{ context === 'castings' ? 'Search pitch requests' : 'Find your next audition' }}
        </p>
        <div class="relative group">
            <Search class="z-10 absolute left-6 top-1/2 -translate-y-1/2 w-[22px] h-[22px] text-white/50 group-focus-within:text-[#0052cc] transition-colors duration-300" />
            <input type="text"
                   class="w-full h-16 bg-white/[0.07] backdrop-blur-xl border-2 border-white/40 text-white text-[20px] font-medium rounded-[20px] pl-16 pr-16 focus:outline-none focus:bg-white/[0.10] focus:border-white/70 focus:shadow-[0_0_0_4px_rgba(255,255,255,0.08),0_0_40px_rgba(255,255,255,0.1)] transition-all duration-300"
                   :value="searchQuery"
                   @input="$emit('searchInput', $event)"
                   @focus="$emit('searchFocus')"
            />
            <Transition name="placeholder-fade" mode="out-in">
                <span v-if="!searchQuery"
                      :key="currentPlaceholder"
                      class="pointer-events-none absolute left-16 top-1/2 -translate-y-1/2 text-white/50 text-[20px] font-medium select-none"
                >
                    {{ currentPlaceholder }}
                </span>
            </Transition>
            <button v-if="searchQuery"
                    @click="$emit('clearSearch')"
                    class="absolute right-5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-all duration-150"
                    aria-label="Clear search"
            >
                <X class="w-4.5 h-4.5 text-white/80" />
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Search, X } from 'lucide-vue-next'

const props = defineProps({
    searchQuery: {
        type: String,
        default: ''
    },
    context: {
        type: String,
        default: 'auditions',
    },
})

defineEmits(['searchInput', 'clearSearch', 'searchFocus'])

const auditionsPlaceholders = [
    'Search auditions...',
    'Search performers...',
    'Find your favorites...',
    'Search by audition titles...',
]

const castingsPlaceholders = [
    'Search pitches...',
    'Search by title...',
    'Search by performer...',
]

const placeholders = computed(() => props.context === 'castings' ? castingsPlaceholders : auditionsPlaceholders)

const currentIndex = ref(0)
const currentPlaceholder = computed(() => placeholders.value[currentIndex.value])
let interval = null

onMounted(() => {
    interval = setInterval(() => {
        currentIndex.value = (currentIndex.value + 1) % placeholders.value.length
    }, 2000)
})

onUnmounted(() => {
    clearInterval(interval)
})
</script>

<style scoped>
.placeholder-fade-enter-active,
.placeholder-fade-leave-active {
    transition: opacity 0.4s ease, transform 0.4s ease;
}

.placeholder-fade-enter-from {
    opacity: 0;
    transform: translateY(-50%) translateX(10px);
}

.placeholder-fade-enter-to {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
}

.placeholder-fade-leave-from {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
}

.placeholder-fade-leave-to {
    opacity: 0;
    transform: translateY(-50%) translateX(-10px);
}
</style>
